import type { LucideIcon, LucideProps } from "lucide-react";
import {
  AlertCircle as LucideAlertCircle,
  AlertTriangle as LucideAlertTriangle,
  ArrowDown as LucideArrowDown,
  ArrowLeft as LucideArrowLeft,
  ArrowRight as LucideArrowRight,
  BarChart3 as LucideBarChart3,
  BookOpen as LucideBookOpen,
  Bot as LucideBot,
  Brain as LucideBrain,
  Building2 as LucideBuilding2,
  Camera as LucideCamera,
  Check as LucideCheck,
  CheckCircle as LucideCheckCircle,
  ChevronDown as LucideChevronDown,
  ChevronLeft as LucideChevronLeft,
  ChevronRight as LucideChevronRight,
  ChevronUp as LucideChevronUp,
  ClipboardType as LucideClipboardType,
  Clock as LucideClock,
  Copy as LucideCopy,
  Database as LucideDatabase,
  DollarSign as LucideDollarSign,
  Download as LucideDownload,
  ExternalLink as LucideExternalLink,
  Eye as LucideEye,
  File as LucideFile,
  FileDown as LucideFileDown,
  FileText as LucideFileText,
  Filter as LucideFilter,
  FlaskConical as LucideFlaskConical,
  FolderOpen as LucideFolderOpen,
  FolderTree as LucideFolderTree,
  GitBranch as LucideGitBranch,
  Globe as LucideGlobe,
  GraduationCap as LucideGraduationCap,
  GripVertical as LucideGripVertical,
  HelpCircle as LucideHelpCircle,
  Headphones as LucideHeadphones,
  History as LucideHistory,
  Home as LucideHome,
  Image as LucideImage,
  Info as LucideInfo,
  Key as LucideKey,
  Languages as LucideLanguages,
  LayoutGrid as LucideLayoutGrid,
  Link as LucideLink,
  Link2 as LucideLink2,
  List as LucideList,
  ListChecks as LucideListChecks,
  Loader2 as LucideLoader2,
  Lock as LucideLock,
  LogIn as LucideLogIn,
  LogOut as LucideLogOut,
  Mail as LucideMail,
  Maximize as LucideMaximize,
  Maximize2 as LucideMaximize2,
  Menu as LucideMenu,
  MessageCircle as LucideMessageCircle,
  Mic as LucideMic,
  MicOff as LucideMicOff,
  Minimize2 as LucideMinimize2,
  Paperclip as LucidePaperclip,
  Pencil as LucidePencil,
  Play as LucidePlay,
  Plus as LucidePlus,
  Puzzle as LucidePuzzle,
  PlusCircle as LucidePlusCircle,
  RefreshCw as LucideRefreshCw,
  RotateCcw as LucideRotateCcw,
  Save as LucideSave,
  Search as LucideSearch,
  Send as LucideSend,
  SendHorizontal as LucideSendHorizontal,
  Settings as LucideSettings,
  Shield as LucideShield,
  ShieldCheck as LucideShieldCheck,
  Smartphone as LucideSmartphone,
  Sparkle as LucideSparkle,
  TestTube as LucideTestTube,
  ThumbsUp as LucideThumbsUp,
  Timer as LucideTimer,
  Trash as LucideTrash,
  Unlink as LucideUnlink,
  User as LucideUser,
  Users as LucideUsers,
  Video as LucideVideo,
  Wrench as LucideWrench,
  X as LucideX,
  XCircle as LucideXCircle,
  Zap as LucideZap,
} from "lucide-react";

export type AppIconWeight =
  | "thin"
  | "light"
  | "regular"
  | "bold"
  | "fill"
  | "duotone"
  | (string & {});

export type AppIconProps = LucideProps & {
  // Compatibility shim: many existing components pass Phosphor-style weights.
  // Lucide doesn't support these variants, so we map weight -> strokeWidth.
  weight?: AppIconWeight;
};

function strokeWidthFromWeight(weight?: AppIconWeight): number | undefined {
  switch (weight) {
    case "thin":
      return 1;
    case "light":
      return 1.5;
    case "regular":
    case "duotone":
      return 2;
    case "bold":
    case "fill":
      return 2.5;
    default:
      return undefined;
  }
}

function wrap(icon: LucideIcon) {
  return function AppIcon({ weight, strokeWidth, ...props }: AppIconProps) {
    const resolvedStrokeWidth =
      strokeWidth ?? strokeWidthFromWeight(weight) ?? undefined;
    const Icon = icon;
    return <Icon {...props} strokeWidth={resolvedStrokeWidth} />;
  };
}

export const ArrowCounterClockwise = wrap(LucideRotateCcw);
export const ArrowDown = wrap(LucideArrowDown);
export const ArrowLeft = wrap(LucideArrowLeft);
export const ArrowRight = wrap(LucideArrowRight);
export const ArrowsClockwise = wrap(LucideRefreshCw);
export const ArrowsInSimple = wrap(LucideMinimize2);
export const ArrowsOut = wrap(LucideMaximize);
export const ArrowsOutSimple = wrap(LucideMaximize2);
export const ArrowSquareOut = wrap(LucideExternalLink);
export const BookOpen = wrap(LucideBookOpen);
export const Brain = wrap(LucideBrain);
export const Buildings = wrap(LucideBuilding2);
export const Camera = wrap(LucideCamera);
export const CaretDown = wrap(LucideChevronDown);
export const CaretLeft = wrap(LucideChevronLeft);
export const CaretRight = wrap(LucideChevronRight);
export const CaretUp = wrap(LucideChevronUp);
export const ChartBar = wrap(LucideBarChart3);
export const ChatCircle = wrap(LucideMessageCircle);
export const Check = wrap(LucideCheck);
export const CheckCircle = wrap(LucideCheckCircle);
export const CircleNotch = wrap(LucideLoader2);
export const ClipboardText = wrap(LucideClipboardType);
export const Clock = wrap(LucideClock);
export const ClockCounterClockwise = wrap(LucideHistory);
export const Copy = wrap(LucideCopy);
export const CurrencyDollar = wrap(LucideDollarSign);
export const Database = wrap(LucideDatabase);
export const DotsSixVertical = wrap(LucideGripVertical);
export const DownloadSimple = wrap(LucideDownload);
export const Envelope = wrap(LucideMail);
export const Eye = wrap(LucideEye);
export const File = wrap(LucideFile);
export const FileArrowDown = wrap(LucideFileDown);
export const FileDoc = wrap(LucideFileText);
export const FilePdf = wrap(LucideFileText);
export const FileText = wrap(LucideFileText);
export const Flask = wrap(LucideFlaskConical);
export const FloppyDisk = wrap(LucideSave);
export const FolderOpen = wrap(LucideFolderOpen);
export const Funnel = wrap(LucideFilter);
export const Gear = wrap(LucideSettings);
export const GitBranch = wrap(LucideGitBranch);
export const Globe = wrap(LucideGlobe);
export const GraduationCap = wrap(LucideGraduationCap);
export const Headset = wrap(LucideHeadphones);
export const House = wrap(LucideHome);
export const Image = wrap(LucideImage);
export const Info = wrap(LucideInfo);
export const Key = wrap(LucideKey);
export const Lightning = wrap(LucideZap);
export const Link = wrap(LucideLink);
export const LinkBreak = wrap(LucideUnlink);
export const LinkSimple = wrap(LucideLink2);
export const List = wrap(LucideMenu);
export const ListBullets = wrap(LucideList);
export const ListChecks = wrap(LucideListChecks);
export const Lock = wrap(LucideLock);
export const MagnifyingGlass = wrap(LucideSearch);
export const Microphone = wrap(LucideMic);
export const MicrophoneSlash = wrap(LucideMicOff);
export const Paperclip = wrap(LucidePaperclip);
export const PaperPlaneRight = wrap(LucideSendHorizontal);
export const PaperPlaneTilt = wrap(LucideSend);
export const PencilSimple = wrap(LucidePencil);
export const Play = wrap(LucidePlay);
export const Plus = wrap(LucidePlus);
export const PlusCircle = wrap(LucidePlusCircle);
export const Puzzle = wrap(LucidePuzzle);
export const Question = wrap(LucideHelpCircle);
export const Robot = wrap(LucideBot);
export const Shield = wrap(LucideShield);
export const ShieldCheck = wrap(LucideShieldCheck);
export const SignIn = wrap(LucideLogIn);
export const SignOut = wrap(LucideLogOut);
export const Smartphone = wrap(LucideSmartphone);
export const Sparkle = wrap(LucideSparkle);
export const SquaresFour = wrap(LucideLayoutGrid);
export const TestTube = wrap(LucideTestTube);
export const ThumbsUp = wrap(LucideThumbsUp);
export const Timer = wrap(LucideTimer);
export const Translate = wrap(LucideLanguages);
export const Trash = wrap(LucideTrash);
export const TreeStructure = wrap(LucideFolderTree);
export const User = wrap(LucideUser);
export const Users = wrap(LucideUsers);
export const VideoCamera = wrap(LucideVideo);
export const Warning = wrap(LucideAlertTriangle);
export const WarningCircle = wrap(LucideAlertCircle);
export const Wrench = wrap(LucideWrench);
export const X = wrap(LucideX);
export const XCircle = wrap(LucideXCircle);
